import { Text } from 'src/design/atoms/Text';
import { IconArchive } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { JobDto } from 'src/api/FiretrolApiClient';
import { useArchiveJobs } from 'src/api/queries/jobs/useJobMutations';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { useAuth } from 'src/hooks/useAuth';

interface ClosedJobTableProps {
  jobs: JobDto[];
  loading: boolean;
}

export const ClosedJobTable: React.FC<ClosedJobTableProps> = ({
  jobs,
  loading,
}) => {
  const navigate = useNavigate();
  const { checkFeatureAllowed } = useAuth();
  const [selectedRowIds, setSelectedRowIds] = React.useState<
    Record<string, boolean>
  >({});
  const archiveJobs = useArchiveJobs();

  const handleView = (row: JobDto) => {
    navigate(`/jobs/${row.id}/summary`);
  };

  const archiveSelectedJobs = async () => {
    const jobIds = Object.keys(selectedRowIds).map((id) => parseInt(id));
    await archiveJobs.mutateAsync({ jobIds });
    setSelectedRowIds({});
  };

  const canArchive = checkFeatureAllowed([
    'jobs_settings_all',
    'jobs_settings_archive_jobs',
  ]);

  const columns = React.useMemo<MRT_ColumnDef<JobDto>[]>(
    () => [
      {
        id: 'job',
        accessorFn: (row) => `${row.jobNumber} - ${row.name}`,
        header: 'Job',
        size: 325,
      },
      {
        id: 'division',
        accessorFn: (row) => row.divisionEntity?.name || row.division || '--',
        header: 'Division',
        size: 150,
      },
      {
        id: 'milestone',
        accessorFn: (row) => row.milestone?.title || '--',
        header: 'Milestone',
        size: 150,
      },
      {
        id: 'customerName',
        accessorFn: (row) => row.customerName || '--',
        header: 'Customer',
        size: 200,
      },
      {
        id: 'description',
        accessorFn: (row) =>
          row.description && row.description.length > 0
            ? row.description
            : '--',
        header: 'Description',
        size: 300,
        Cell: ({ cell }) => (
          <Text
            c="black"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cell.getValue<string>()}
          </Text>
        ),
      },
      {
        id: 'location',
        accessorFn: (row) => row.location?.address || '--',
        header: 'Jobsite Location',
        size: 250,
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'closed-jobs-table',
    columns: columns,
    defaultSorting: [{ id: 'job', desc: false }],
  });

  const tableExports = useTableExports<JobDto>('closed-jobs', (data) => {
    return data.map((item) => ({
      ['Job Number']: item.jobNumber,
      ['Job Name']: item.name,
      ['Division']: item.divisionEntity?.name || item.division || '',
      ['Milestone']: item.milestone?.title || '',
      ['Customer']: item.customerName || '',
      ['Description']: item.description || '',
      ['Jobsite Location']: item.location?.address || '',
    }));
  });

  const { table } = useTable({
    ...tableState,
    rowSelection: selectedRowIds,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => (
        <ViewActionIcon onClick={() => handleView(row.original)} />
      ),
      enableRowSelection: canArchive,
      enableClickToCopy: false,
      columns: columns,
      data: jobs,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      onRowSelectionChange: setSelectedRowIds,
      renderTopToolbarCustomActions: () => (
        <>
          {canArchive && (
            <BasicButton
              disabled={Object.keys(selectedRowIds).length === 0}
              variant="filled"
              color="red"
              leftSection={<IconArchive />}
              onClick={archiveSelectedJobs}
              loading={archiveJobs.isPending}
            >
              Archive Selected Jobs ({Object.keys(selectedRowIds).length})
            </BasicButton>
          )}
        </>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: jobs.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return <>{table}</>;
};
